<template>
  <b-sidebar
    id="view-hs-code-sidebar"
    :visible="!!selectedHSCodeId"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => !val && $emit('close')"
    @hidden="$emit('close')"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">View HS Code</h5>

        <feather-icon class="ml-1 cursor-pointer" icon="XIcon" size="16" @click="hide" />
      </div>

      <!-- BODY -->
      <b-overlay :show="isLoading" spinner-variant="primary">
        <div class="p-2">
          <!-- HS Code Details -->
          <div v-if="hsCodeData" class="hs-code-details">
            <!-- ID -->
            <div v-if="hsCodeData.id" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">ID:</label>
              <span class="detail-value">{{ hsCodeData.id }}</span>
            </div>

            <!-- Code -->
            <div v-if="hsCodeData.code" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Code:</label>
              <span class="detail-value">{{ hsCodeData.code }}</span>
            </div>

            <!-- Name -->
            <div v-if="hsCodeData.name" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Name:</label>
              <span class="detail-value">{{ hsCodeData.name }}</span>
            </div>

            <!-- Description -->
            <div v-if="hsCodeData.description" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Description:</label>
              <span class="detail-value">{{ hsCodeData.description }}</span>
            </div>

            <!-- Status -->
            <div v-if="hsCodeData.isActive !== undefined" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Status:</label>
              <b-badge :variant="hsCodeData.isActive ? 'success' : 'danger'" class="detail-value">
                {{ hsCodeData.isActive ? 'Active' : 'Inactive' }}
              </b-badge>
            </div>

            <!-- Business ID -->
            <div v-if="hsCodeData.businessID" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Business ID:</label>
              <span class="detail-value">{{ hsCodeData.businessID }}</span>
            </div>

            <!-- Created By -->
            <div v-if="hsCodeData.metaAddedBy || hsCodeData.addedBy" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Created By:</label>
              <span class="detail-value">{{ hsCodeData.metaAddedBy || hsCodeData.addedBy }}</span>
            </div>

            <!-- Created On -->
            <div v-if="hsCodeData.addedOn" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Created On:</label>
              <span class="detail-value">{{ formatDate(hsCodeData.addedOn) }}</span>
            </div>

            <!-- Updated By -->
            <div v-if="hsCodeData.metaUpdatedBy || hsCodeData.updatedBy" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Updated By:</label>
              <span class="detail-value">{{
                hsCodeData.metaUpdatedBy || hsCodeData.updatedBy
              }}</span>
            </div>

            <!-- Updated On -->
            <div v-if="hsCodeData.updatedOn" class="detail-row mb-3">
              <label class="detail-label font-weight-bold">Updated On:</label>
              <span class="detail-value">{{ formatDate(hsCodeData.updatedOn) }}</span>
            </div>
          </div>

          <!-- No Data Message -->
          <div v-else-if="!isLoading" class="text-center text-muted">
            <feather-icon icon="AlertCircleIcon" size="48" class="mb-2" />
            <p>No HS Code data found</p>
          </div>

          <!-- Action Buttons -->
          <div class="text-center mt-4">
            <b-button variant="outline-secondary" @click="hide"> Close </b-button>
          </div>
        </div>
      </b-overlay>
    </template>
  </b-sidebar>
</template>

<script>
import { BSidebar, BOverlay, BButton, BBadge } from 'bootstrap-vue';
import { ref, watch } from '@vue/composition-api';
import store from '@/store';

export default {
  name: 'HSCodeListView',
  components: {
    BSidebar,
    BOverlay,
    BButton,
    BBadge,
  },
  props: {
    selectedHSCodeId: {
      type: [Number],
      default: null,
    },
  },
  setup(props, { emit }) {
    const isLoading = ref(false);
    const hsCodeData = ref(null);

    const fetchHSCodeData = async () => {
      if (!props.selectedHSCodeId) return;

      isLoading.value = true;
      try {
        const response = await store.dispatch('fbr-invoice-hs-code/fetchHSCode', {
          id: props.selectedHSCodeId,
        });

        console.log('View HS Code data response:', response);

        // Handle different possible response structures
        let fetchedHSCode = null;

        if (Array.isArray(response) && response.length > 0) {
          // If response is an array, take the first item
          fetchedHSCode = response[0];
        } else if (response && response.hsCode) {
          // If response has hsCode wrapper
          fetchedHSCode = response.hsCode;
        } else if (response && response.id) {
          // If response is the direct hsCode object
          fetchedHSCode = response;
        } else if (response && response.data && response.data.hsCode) {
          // If response has nested data structure
          fetchedHSCode = response.data.hsCode;
        } else if (response && response.data && response.data.id) {
          // If response.data is the direct hsCode object
          fetchedHSCode = response.data;
        } else {
          console.error('Unexpected response structure:', response);
          throw new Error('Invalid response structure from API');
        }

        hsCodeData.value = fetchedHSCode;
        console.log('=== HSCodeListView populated data:', hsCodeData.value);
      } catch (error) {
        console.error('Error fetching HS Code:', error);
        hsCodeData.value = null;
      } finally {
        isLoading.value = false;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      try {
        return new Date(dateString).toLocaleString();
      } catch (error) {
        return dateString;
      }
    };

    // Watch for selectedHSCodeId changes to fetch data
    watch(
      () => props.selectedHSCodeId,
      (newId) => {
        if (newId) {
          fetchHSCodeData();
        } else {
          hsCodeData.value = null;
        }
      },
      { immediate: true }
    );

    return {
      hsCodeData,
      isLoading,
      formatDate,
    };
  },
};
</script>

<style lang="scss">
#view-hs-code-sidebar {
  .content-sidebar-header {
    padding: 1rem 1.5rem !important;
  }

  .hs-code-details {
    .detail-row {
      display: flex;
      align-items: flex-start;

      .detail-label {
        min-width: 120px;
        margin-right: 1rem;
        color: #6e6b7b;
      }

      .detail-value {
        flex: 1;
        word-break: break-word;
      }
    }
  }
}
</style>
